/**
 * Wetter-Seite: alles, was für die Pflanzen am Fenster zählt –
 * Sonne, Gewitter, Regen, Luftfeuchte drinnen und klare Tipps
 * (Fenster auf/zu, Vorhang auf/zu, befeuchten).
 */
import { RefreshCw } from 'lucide-react';
import {
  WARNING_LABEL,
  WARNING_LEVEL_LABEL,
  compassLabel,
  fmtNumber,
  fmtTime,
  relevantWarnings,
  zonedParts,
  type Room,
  type WeatherData,
} from '@pflanzen/shared';
import { useStore, setRoomState } from '../lib/store';
import { useWeather, type WeatherView } from '../lib/weather';
import { haptic } from '../lib/haptics';
import { AdviceCard, condition } from '../ui/WeatherBits';
import { Card, SectionTitle, cx } from '../ui/kit';

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex flex-col rounded-2xl bg-paper p-3">
      <div className="min-h-8 text-xs leading-4 text-muted">{label}</div>
      <div className="font-display text-xl">{value}</div>
      {hint && <div className="mt-auto pt-0.5 text-[11px] leading-4 text-muted">{hint}</div>}
    </div>
  );
}

function uvText(uv: number): string {
  if (uv < 3) return 'niedrig';
  if (uv < 6) return 'mäßig';
  if (uv < 8) return 'hoch';
  return 'sehr hoch';
}

function NowCard({ view, tz }: { view: WeatherView; tz: string }) {
  const { weather: w, climate } = view;
  const c = condition(w.now.code, w.now.isDay);
  const today = w.days[0];
  return (
    <Card className="rise">
      <div className="flex items-center gap-3">
        <div className="text-5xl leading-none">{c.icon}</div>
        <div>
          <div className="font-display text-4xl">{Math.round(w.now.temp)}°</div>
          <div className="text-sm text-muted">
            {c.label}
            {today ? ` · ${Math.round(today.tMin)}° / ${Math.round(today.tMax)}°` : ''}
          </div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Stat label="Luftfeuchte drinnen (Schätzung)" value={`${climate.indoorRh} %`} hint={climate.heating ? 'Heizperiode' : 'ohne Heizung'} />
        <Stat label="Luftfeuchte draußen" value={`${Math.round(w.now.rh)} %`} hint={`beim Lüften drinnen ca. ${climate.indoorRhIfAired} %`} />
        <Stat label="UV-Index" value={fmtNumber(w.now.uv, 1)} hint={uvText(w.now.uv)} />
        <Stat
          label="Sonnenstrahlung"
          value={w.now.isDay ? `${Math.round(w.now.ghi)} W/m²` : '–'}
          hint={!w.now.isDay ? 'Nacht' : w.now.dni > 400 ? 'pralle Sonne' : w.now.dni > 150 ? 'sonnig' : 'diffus'}
        />
        <Stat label="Wind / Böen" value={`${Math.round(w.now.gusts)} km/h`} hint={`Wind ${Math.round(w.now.wind)} km/h`} />
        <Stat label="Tageslicht" value={`${fmtNumber(climate.dayLengthH, 1)} h`} hint={today ? `${fmtTime(new Date(today.sunrise), tz)} – ${fmtTime(new Date(today.sunset), tz)}` : ''} />
      </div>
      <p className="mt-2 text-[11px] text-muted">
        Stand {fmtTime(new Date(w.fetchedAt), tz)} · {w.source}
      </p>
    </Card>
  );
}

const WINDOW_OPTS: { key: Room['window']; label: string }[] = [
  { key: 'closed', label: 'Zu' },
  { key: 'tilted', label: 'Gekippt' },
  { key: 'open', label: 'Offen' },
];
const CURTAIN_OPTS: { key: Room['curtain']; label: string }[] = [
  { key: 'closed', label: 'Zu' },
  { key: 'open', label: 'Offen' },
];

function Segmented<T extends string>({ value, options, onChange }: { value: T; options: { key: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <div className="grid rounded-2xl bg-paper p-1" style={{ gridTemplateColumns: `repeat(${options.length}, 1fr)` }}>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => {
            haptic();
            onChange(o.key);
          }}
          className={cx('min-h-10 rounded-xl text-sm font-semibold transition', value === o.key ? 'bg-card text-ink shadow-sm' : 'text-muted')}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function RoomCard({ room, view, tz }: { room: Room; view: WeatherView; tz: string }) {
  const sunNow = view.climate.sunOnPlants[room.id];
  const until = view.climate.sunUntil[room.id];
  const line = view.timelines[room.id] ?? [];
  return (
    <Card className="rise">
      <div className="mb-3 flex items-baseline justify-between">
        <h3 className="font-display text-xl">{room.name}</h3>
        {room.windowAzimuth != null && <span className="text-xs text-muted">Fenster nach {compassLabel(room.windowAzimuth)}</span>}
      </div>
      <div className="grid grid-cols-[72px_1fr] items-center gap-x-3 gap-y-2">
        <span className="text-sm text-muted">Fenster</span>
        <Segmented value={room.window} options={WINDOW_OPTS} onChange={(v) => void setRoomState(room.id, { window: v })} />
        <span className="text-sm text-muted">Vorhang</span>
        <Segmented value={room.curtain} options={CURTAIN_OPTS} onChange={(v) => void setRoomState(room.id, { curtain: v })} />
      </div>
      <div className="mt-4">
        <div className="mb-1.5 text-sm">
          {sunNow ? '☀️ Die Sonne scheint gerade auf die Pflanzen' : until ? `☀️ Heute Sonne auf den Pflanzen bis ca. ${fmtTime(new Date(until), tz)}` : '🌥️ Gerade keine direkte Sonne auf den Pflanzen'}
        </div>
        <div className="flex gap-[3px]" aria-label="Sonne auf den Pflanzen, nächste Stunden">
          {line.map((h, i) => (
            <div key={h.time} className="flex-1">
              <div className={cx('h-6 rounded-md', h.onPlants ? (h.strong ? 'bg-sun' : 'bg-sun/45') : 'bg-line')} />
              <div className="mt-0.5 h-3.5 text-center text-[11px] leading-none text-muted">{i % 3 === 0 ? zonedParts(new Date(h.time), tz).hour : ''}</div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function HourStrip({ w, tz }: { w: WeatherData; tz: string }) {
  const now = Date.now();
  const hours = w.hours.filter((h) => Date.parse(h.time) >= now - 3_600_000).slice(0, 24);
  return (
    <div className="-mx-4 overflow-x-auto px-4">
      <div className="flex gap-2">
        {hours.map((h) => {
          const c = condition(h.code, h.isDay);
          const thunder = h.cape > 800 && h.precipProb > 30;
          return (
            <div key={h.time} className="flex w-14 shrink-0 flex-col items-center rounded-2xl border border-line bg-card py-2">
              <div className="text-[11px] text-muted">{fmtTime(new Date(h.time), tz)}</div>
              <div className="my-1 text-xl">{thunder ? '⛈️' : c.icon}</div>
              <div className="text-sm font-semibold">{Math.round(h.temp)}°</div>
              <div className={cx('text-[11px]', h.precipProb >= 50 ? 'text-water font-semibold' : 'text-muted')}>{h.precipProb} %</div>
              <div className="text-[10px] text-muted">UV {Math.round(h.uv)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DayList({ w }: { w: WeatherData }) {
  const fmt = new Intl.DateTimeFormat('de-AT', { weekday: 'short', day: 'numeric', month: 'numeric' });
  return (
    <Card className="divide-y divide-line py-1">
      {w.days.map((d) => {
        const c = condition(d.code, true);
        return (
          <div key={d.date} className="flex items-center gap-3 py-2.5">
            <div className="w-20 text-sm">{fmt.format(new Date(`${d.date}T12:00:00Z`))}</div>
            <div className="text-xl">{c.icon}</div>
            <div className="flex-1 text-xs text-muted">
              ☀️ {d.sunshineH.toFixed(0)} h · UV {Math.round(d.uvMax)}{d.precipProbMax >= 30 ? ` · 💧 ${d.precipProbMax} %` : ''}
            </div>
            <div className="text-sm font-semibold">
              {Math.round(d.tMin)}° <span className="text-muted">/</span> {Math.round(d.tMax)}°
            </div>
          </div>
        );
      })}
    </Card>
  );
}

export function WeatherPage() {
  const { state } = useStore();
  const { data, reload } = useWeather();
  if (!state) return null;
  const tz = state.settings.timezone;
  // Nur Warnungen, die gelten oder in den nächsten 24 h beginnen
  const activeWarnings = data ? relevantWarnings(data.weather, new Date(), 24) : [];
  return (
    <div className="px-4 pb-6">
      <header className="pt-safe flex items-end justify-between pb-3">
        <div>
          <p className="text-sm text-muted">{state.settings.location.name}</p>
          <h1 className="font-display text-[34px] leading-tight">Wetter</h1>
        </div>
        <button type="button" onClick={reload} aria-label="Aktualisieren" className="grid h-11 w-11 place-items-center rounded-full bg-card active:scale-95">
          <RefreshCw className="h-5 w-5" />
        </button>
      </header>
      {!data ? (
        <p className="text-sm text-muted">Wetter wird geladen … (braucht Internet)</p>
      ) : (
        <>
          {activeWarnings.length > 0 && (
            <div className="mb-3 space-y-2">
              {activeWarnings.map((wn) => (
                <div key={wn.id} className="rounded-3xl border border-alert/30 bg-alert-soft p-3.5">
                  <div className="text-[15px] font-semibold">
                    ⚠️ Warnung {WARNING_LEVEL_LABEL[wn.level] ?? ''}: {WARNING_LABEL[wn.type]}
                  </div>
                  <div className="text-sm">
                    {fmtTime(new Date(wn.start), tz)} – {fmtTime(new Date(wn.end), tz)} · {wn.text}
                  </div>
                  <div className="mt-1 text-[11px] text-muted">Quelle: GeoSphere Austria</div>
                </div>
              ))}
            </div>
          )}
          <SectionTitle>Tipps für deine Pflanzen</SectionTitle>
          <div className="space-y-2.5">
            {data.advice.map((a) => (
              <AdviceCard key={a.id} advice={a} />
            ))}
          </div>
          <SectionTitle>Fenster & Vorhang</SectionTitle>
          <p className="-mt-1 mb-2.5 px-1 text-xs text-muted">Stell ein, wie es gerade ist – die Tipps passen sich an.</p>
          <div className="space-y-2.5">
            {state.rooms.map((r) => (
              <RoomCard key={r.id} room={r} view={data} tz={tz} />
            ))}
          </div>
          <SectionTitle>Jetzt</SectionTitle>
          <NowCard view={data} tz={tz} />
          <SectionTitle>Nächste Stunden</SectionTitle>
          <HourStrip w={data.weather} tz={tz} />
          <SectionTitle>7 Tage</SectionTitle>
          <DayList w={data.weather} />
        </>
      )}
    </div>
  );
}
